import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowRight, Sparkles, Calculator, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { requestAccessCsv } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { validateEmail } from "@/lib/emailValidation";

export const CTASection = () => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) {
      return;
    }

    const validation = validateEmail(email);
    if (!validation.valid) {
      toast({
        variant: "destructive",
        title: "Invalid email",
        description: validation.message,
        duration: 5000,
      });
      return;
    }
    try {
      const response : any =  await requestAccessCsv(email);
      if(response && response?.status && response?.status >= 400) {
        throw new Error(response.statusText);
      }
      setIsSubmitted(true);
      toast({
        title: "🎉 You're on the list!",
        description: "Welcome aboard, future bug-free hero! We'll notify you when AI starts crushing your production bugs.",
        duration: 5000,
      });
    } catch (err) {
      console.error(err);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please refresh the page and try again.",
        duration: 5000,
      });
    }
  };

  return (
    <section className="py-16 sm:py-24 px-4 bg-muted/30">
      <div className="container mx-auto max-w-3xl">
        <div className="border border-border rounded-xl p-8 sm:p-12 bg-background">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 rounded-full bg-muted border border-border mb-6">
              <Sparkles className="w-3.5 h-3.5 text-foreground/60" />
              <span className="text-xs sm:text-sm font-medium text-foreground/80">Early Access</span>
            </div>

            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6 tracking-tight">
              Build smarter systems, not longer nights.
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground mb-8 sm:mb-10 max-w-xl mx-auto font-light">
              Join the waitlist and be among the first to experience AI-powered bug fixing. 
              Get 100 free fixes when we launch.
            </p>

            {!isSubmitted ? (<form onSubmit={handleSubmit} className="max-w-md mx-auto mb-8">
              <div className="flex flex-col sm:flex-row gap-3">
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 h-12 text-base bg-background border-border focus:border-foreground"
                  disabled={isSubmitting}
                />
                <Button 
                  type="submit" 
                  variant="accent" 
                  size="lg"
                  className="h-12 px-8 hover:scale-105 transition-transform"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Joining..." : "Join Waitlist"}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </form>) : 
            (<div className="flex items-center gap-3 px-6 py-4 rounded-lg bg-accent/10 border border-accent/30 w-full">
              <CheckCircle2 className="w-6 h-6 text-accent flex-shrink-0" />
              <div className="text-left">
                <p className="font-medium text-foreground">You're in! 🚀</p>
                <p className="text-sm text-muted-foreground">We'll reach out when it's your turn to experience AI-powered debugging magic.</p>
              </div>
            </div>)}

            <div className="flex flex-wrap justify-center gap-4 sm:gap-6 text-xs sm:text-sm text-muted-foreground mb-6">
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-foreground/40" />
                <span>No credit card required</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-foreground/40" />
                <span>100 free fixes included</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-foreground/40" />
                <span>Launch: Q1 2026</span>
              </div>
            </div>

            <Link to="/cost-calculator">
              <Button variant="outline" size="lg" className="gap-2">
                <Calculator className="w-4 h-4" />
                Calculate Your Savings
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};